'use client';

import _defineProperty from "/codebuild/output/src3766/src/s3/01/node_modules/@babel/runtime/helpers/esm/defineProperty.js";
import _slicedToArray from "/codebuild/output/src3766/src/s3/01/node_modules/@babel/runtime/helpers/esm/slicedToArray.js";
import _objectWithoutProperties from "/codebuild/output/src3766/src/s3/01/node_modules/@babel/runtime/helpers/esm/objectWithoutProperties.js";
var _excluded = ["sizes", "hasThumb", "onThumbChange", "onThumbPointerUp", "onThumbPointerDown", "onThumbPositionChange", "onDragScroll", "onWheelScroll", "onResize"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { jsx } from 'react/jsx-runtime';
import { forwardRef, useState, useRef, useEffect } from 'react';
import { useMergedRef, useCallbackRef, useDebouncedCallback } from '@mantine/hooks';
import { useScrollAreaContext } from '../ScrollArea.context.mjs';
import { useResizeObserver } from '../use-resize-observer.mjs';
import { composeEventHandlers } from '../utils/compose-event-handlers.mjs';
import { ScrollbarProvider } from './Scrollbar.context.mjs';
var Scrollbar = forwardRef(function (props, forwardedRef) {
  var sizes = props.sizes,
    hasThumb = props.hasThumb,
    onThumbChange = props.onThumbChange,
    onThumbPointerUp = props.onThumbPointerUp,
    onThumbPointerDown = props.onThumbPointerDown,
    onThumbPositionChange = props.onThumbPositionChange,
    onDragScroll = props.onDragScroll,
    onWheelScroll = props.onWheelScroll,
    onResize = props.onResize,
    scrollbarProps = _objectWithoutProperties(props, _excluded);
  var context = useScrollAreaContext();
  var _useState = useState(null),
    _useState2 = _slicedToArray(_useState, 2),
    scrollbar = _useState2[0],
    setScrollbar = _useState2[1];
  var composeRefs = useMergedRef(forwardedRef, function (node) {
    return setScrollbar(node);
  });
  var rectRef = useRef(null);
  var prevWebkitUserSelectRef = useRef("");
  var viewport = context.viewport;
  var maxScrollPos = sizes.content - sizes.viewport;
  var handleWheelScroll = useCallbackRef(onWheelScroll);
  var handleThumbPositionChange = useCallbackRef(onThumbPositionChange);
  var handleResize = useDebouncedCallback(onResize, 10);
  var handleDragScroll = function handleDragScroll(event) {
    if (rectRef.current) {
      var x = event.clientX - rectRef.current.left;
      var y = event.clientY - rectRef.current.top;
      onDragScroll({
        x: x,
        y: y
      });
    }
  };
  useEffect(function () {
    var handleWheel = function handleWheel(event) {
      var element = event.target;
      var isScrollbarWheel = scrollbar === null || scrollbar === void 0 ? void 0 : scrollbar.contains(element);
      if (isScrollbarWheel) handleWheelScroll(event, maxScrollPos);
    };
    document.addEventListener("wheel", handleWheel, {
      passive: false
    });
    return function () {
      return document.removeEventListener("wheel", handleWheel, {
        passive: false
      });
    };
  }, [viewport, scrollbar, maxScrollPos, handleWheelScroll]);
  useEffect(handleThumbPositionChange, [sizes, handleThumbPositionChange]);
  useResizeObserver(scrollbar, handleResize);
  useResizeObserver(context.content, handleResize);
  return /* @__PURE__ */jsx(ScrollbarProvider, {
    value: {
      scrollbar: scrollbar,
      hasThumb: hasThumb,
      onThumbChange: useCallbackRef(onThumbChange),
      onThumbPointerUp: useCallbackRef(onThumbPointerUp),
      onThumbPositionChange: handleThumbPositionChange,
      onThumbPointerDown: useCallbackRef(onThumbPointerDown)
    },
    children: /* @__PURE__ */jsx("div", _objectSpread(_objectSpread({}, scrollbarProps), {}, {
      ref: composeRefs,
      "data-mantine-scrollbar": true,
      style: _objectSpread({
        position: "absolute"
      }, scrollbarProps.style),
      onPointerDown: composeEventHandlers(props.onPointerDown, function (event) {
        event.preventDefault();
        var mainPointer = 0;
        if (event.button === mainPointer) {
          var element = event.target;
          element.setPointerCapture(event.pointerId);
          rectRef.current = scrollbar.getBoundingClientRect();
          prevWebkitUserSelectRef.current = document.body.style.webkitUserSelect;
          document.body.style.webkitUserSelect = "none";
          document.body.style.pointerEvents = "none";
          handleDragScroll(event);
        }
      }),
      onPointerMove: composeEventHandlers(props.onPointerMove, handleDragScroll),
      onPointerUp: composeEventHandlers(props.onPointerUp, function (event) {
        event.preventDefault();
        var element = event.target;
        if (element.hasPointerCapture(event.pointerId)) {
          element.releasePointerCapture(event.pointerId);
        }
        document.body.style.webkitUserSelect = prevWebkitUserSelectRef.current;
        document.body.style.pointerEvents = "auto";
        rectRef.current = null;
      })
    }))
  });
});
export { Scrollbar };